import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import StrategyOverlay from "./components/StrategyOverlay";
import { safeObj } from "./utils/api";
import { apiBase } from "./lib/apiBase";
import { useAuth } from "./lib/AuthContext";
import { fetchJson } from "./lib/api";
import { useRuntimeConfig, CANONICAL_SETTINGS } from "./lib/runtimeConfig";
import summarizeEngineRuntimeModes from "./components/EngineRuntimeModesSummary";

const MODE_OPTIONS = [
    { value: "dry_run", label: "Dry" },
    { value: "paper", label: "Paper" },
    { value: "live", label: "Live" },
];

const ENGINE_DEFINITIONS = [
    {
        key: "nebula_horizon",
        aliases: ["horizon", "horizon_engine"],
        label: "Horizon Engine",
        coinsKey: "horizon",
    },
    {
        key: "scalping_engine",
        aliases: ["scalping"],
        label: "Scalping Engine",
        coinsKey: "scalp",
    },
    {
        key: "signal_engine",
        aliases: ["dream", "dream_nebula", "dream_engine"],
        label: "Dream Engine",
        coinsKey: "dream",
    },
];

const DEFAULT_CONFIG = { global_mode: "paper", engines: {}, engine_runtime_modes: {} };

function normalizePinConfig() {
    const runtimeEnv = globalThis.__IMPORT_META_ENV__ ?? {};
    const envPin = (runtimeEnv?.VITE_SAFETY_CONFIRM_PIN ?? "").trim();
    if (envPin) return envPin;

    const legacyPin = (typeof globalThis !== "undefined" && globalThis.__SAFETY_CONFIRM_PIN__) || "";
    return String(legacyPin || "").trim();
}

function validatePin(inputPin, configuredPin) {
    const normalized = String(inputPin ?? "").trim();
    if (!configuredPin) return { valid: true };
    if (!normalized) return { valid: false, error: "PIN is required." };
    if (!/^\d+$/.test(normalized)) return { valid: false, error: "PIN must contain digits only." };
    if (configuredPin.length && normalized.length !== configuredPin.length) {
        return { valid: false, error: `PIN must be a ${configuredPin.length}-digit code.` };
    }
    if (normalized !== configuredPin) return { valid: false, error: "Incorrect PIN. Try again." };
    return { valid: true };
}

function resolveEngineKey(rawKey = "") {
    const lower = String(rawKey || "").toLowerCase();
    const match = ENGINE_DEFINITIONS.find(
        (engine) => engine.key === lower || engine.aliases.includes(lower),
    );
    return match?.key || lower;
}

function normalizeModes(raw = {}, engineCoins = CANONICAL_SETTINGS.engines) {
    const payload = safeObj(raw);
    const globalMode =
        payload.global_mode || payload.globalMode || payload.mode || DEFAULT_CONFIG.global_mode;
    const engines = safeObj(payload.engine_runtime_modes || payload.engines);
    const normalizedEngines = {};

    ENGINE_DEFINITIONS.forEach((engine) => {
        const existing = Object.entries(engines).find(
            ([key]) => resolveEngineKey(key) === engine.key,
        );
        const source = safeObj(existing?.[1]);
        const coins = Array.isArray(engineCoins?.[engine.coinsKey])
            ? engineCoins[engine.coinsKey]
            : CANONICAL_SETTINGS.engines[engine.coinsKey];
        const next = {};
        coins.forEach((coin) => {
            next[coin] = source?.[coin] || globalMode;
        });
        normalizedEngines[engine.key] = next;
    });

    return { globalMode, engines: normalizedEngines };
}

function modesEqual(a, b) {
    if (!a || !b) return false;
    if (a.globalMode !== b.globalMode) return false;
    const keys = new Set([...Object.keys(a.engines || {}), ...Object.keys(b.engines || {})]);
    for (const key of keys) {
        const aCoins = a.engines?.[key] || {};
        const bCoins = b.engines?.[key] || {};
        const coinKeys = new Set([...Object.keys(aCoins), ...Object.keys(bCoins)]);
        for (const coin of coinKeys) {
            if (aCoins[coin] !== bCoins[coin]) return false;
        }
    }
    return true;
}

function buildSubmitPayload(state) {
    const engines = {};
    ENGINE_DEFINITIONS.forEach((engine) => {
        if (state.engines?.[engine.key]) {
            engines[engine.key] = state.engines[engine.key];
        }
    });
    return { global_mode: state.globalMode, engine_runtime_modes: engines, engines };
}

export default function TradingModeSettings({ open, onClose }) {
    const { logout, markUnauthorized } = useAuth();
    const { config: runtimeConfig } = useRuntimeConfig();
    const [loggingOut, setLoggingOut] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [saveError, setSaveError] = useState("");
    const [loadStatus, setLoadStatus] = useState("idle");
    const [modeState, setModeState] = useState(() => normalizeModes(DEFAULT_CONFIG));
    const [initialState, setInitialState] = useState(() => normalizeModes(DEFAULT_CONFIG));
    const [expanded, setExpanded] = useState(false);
    const [loadingModes, setLoadingModes] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pinModalOpen, setPinModalOpen] = useState(false);
    const [pinValue, setPinValue] = useState("");
    const [pinError, setPinError] = useState("");
    const [pinValidated, setPinValidated] = useState(false);

    const configuredPin = useMemo(() => normalizePinConfig(), []);
    const pinRequired = configuredPin.length > 0;

    const engineCoins = useMemo(
        () => runtimeConfig?.engineCoins || CANONICAL_SETTINGS.engines,
        [runtimeConfig?.engineCoins],
    );

    const coinOrder = useMemo(() => {
        const configuredOrder = (runtimeConfig?.coins || CANONICAL_SETTINGS.symbols).map(
            (c) => (typeof c === "string" ? c : c?.symbol) || c?.slug,
        );
        const seen = new Set();
        const coins = [];
        configuredOrder.forEach((coin) => {
            const key = (coin || "").toUpperCase();
            if (key && !seen.has(key)) {
                seen.add(key);
                coins.push(key);
            }
        });
        return coins;
    }, [runtimeConfig]);

    const scalpingSet = useMemo(
        () => new Set(engineCoins?.scalp || CANONICAL_SETTINGS.engines.scalp),
        [engineCoins?.scalp],
    );

    const horizonSet = useMemo(
        () => new Set(engineCoins?.horizon || CANONICAL_SETTINGS.engines.horizon),
        [engineCoins?.horizon],
    );

    const dreamSet = useMemo(
        () => new Set(engineCoins?.dream || CANONICAL_SETTINGS.engines.dream),
        [engineCoins?.dream],
    );

    const allCoins = useMemo(() => {
        const merged = new Set([
            ...(engineCoins?.horizon || CANONICAL_SETTINGS.engines.horizon),
            ...(engineCoins?.dream || CANONICAL_SETTINGS.engines.dream),
        ]);
        const ordered = [];
        coinOrder.forEach((coin) => {
            if (merged.has(coin)) ordered.push(coin);
        });
        merged.forEach((coin) => {
            if (!ordered.includes(coin)) ordered.push(coin);
        });
        return ordered;
    }, [coinOrder, engineCoins]);

    const loadModes = useCallback(async () => {
        setLoadingModes(true);
        setLoadError("");
        setLoadStatus("loading");
        try {
            const res = await fetchJson(apiBase("trading_mode_status"));
            if (res?.unauth || res?.data?.error === "unauthorized") {
                markUnauthorized();
                setLoadStatus("unauthorized");
                setModeState(normalizeModes(DEFAULT_CONFIG, engineCoins));
                setInitialState(normalizeModes(DEFAULT_CONFIG, engineCoins));
                return;
            }
            if (res?.status === "no_data_yet" || res?.data?.message === "no_data_yet") {
                setLoadStatus("no_data");
                const fallback = normalizeModes(DEFAULT_CONFIG, engineCoins);
                setModeState(fallback);
                setInitialState(fallback);
                return;
            }
            if (!res?.ok || !res?.data) {
                setLoadError("Unable to load settings.");
                setLoadStatus("error");
                const fallback = normalizeModes(DEFAULT_CONFIG, engineCoins);
                setModeState(fallback);
                setInitialState(fallback);
                return;
            }
            const normalized = normalizeModes(res.data, engineCoins);
            setModeState(normalized);
            setInitialState(normalized);
            setLoadStatus("ready");
        } catch {
            setLoadError("Unable to load settings.");
            setLoadStatus("error");
            const fallback = normalizeModes(DEFAULT_CONFIG, engineCoins);
            setModeState(fallback);
            setInitialState(fallback);
        } finally {
            setLoadingModes(false);
        }
    }, [engineCoins, markUnauthorized]);

    useEffect(() => {
        if (expanded) {
            loadModes();
        }
    }, [expanded, loadModes]);

    useEffect(() => {
        const normalizeStateShape = (prev) =>
            normalizeModes(
                {
                    global_mode: prev?.globalMode ?? prev?.global_mode ?? DEFAULT_CONFIG.global_mode,
                    engines: prev?.engines,
                },
                engineCoins,
            );
        setModeState((prev) => normalizeStateShape(prev));
        setInitialState((prev) => normalizeStateShape(prev));
    }, [engineCoins]);

    const handleLogout = async () => {
        setLoggingOut(true);
        try {
            await logout();
        } finally {
            setLoggingOut(false);
            onClose();
        }
    };

    const handleChange = (engineKey, coin, value) => {
        setModeState((prev) => ({
            ...prev,
            engines: {
                ...prev.engines,
                [engineKey]: {
                    ...(prev.engines?.[engineKey] || {}),
                    [coin]: value,
                },
            },
        }));
    };

    const hasChanges = useMemo(() => !modesEqual(modeState, initialState), [modeState, initialState]);

    const summary = useMemo(
        () => summarizeEngineRuntimeModes(modeState, ENGINE_DEFINITIONS, engineCoins),
        [engineCoins, modeState],
    );

    const startPinFlow = () => {
        if (!pinRequired) {
            setPinValidated(true);
            return true;
        }
        setPinModalOpen(true);
        setPinValue("");
        setPinError("");
        return false;
    };

    const submitChanges = useCallback(async () => {
        setSaving(true);
        setSaveError("");
        try {
            const res = await fetchJson(apiBase("trading_mode_status"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(buildSubmitPayload(modeState)),
            });
            if (res?.unauth || res?.data?.error === "unauthorized") {
                markUnauthorized();
                return;
            }
            if (!res?.ok) {
                setSaveError("Failed to update settings.");
                return;
            }
            await loadModes();
            setPinValidated(true);
        } catch {
            setSaveError("Failed to update settings.");
        } finally {
            setSaving(false);
        }
    }, [loadModes, markUnauthorized, modeState]);

    const handleApply = () => {
        if (!hasChanges || saving) return;
        if (pinRequired && !pinValidated) {
            const ready = startPinFlow();
            if (!ready) return;
        }
        submitChanges();
    };

    const handleConfirmPin = () => {
        const result = validatePin(pinValue, configuredPin);
        if (!result.valid) {
            setPinError(result.error || "Incorrect PIN. Please try again.");
            return;
        }
        setPinValidated(true);
        setPinModalOpen(false);
        submitChanges();
    };

    const disabledApply =
        !hasChanges ||
        saving ||
        loadingModes ||
        loadStatus === "idle" ||
        loadStatus === "unauthorized" ||
        loadStatus === "error";

    const renderCell = (engineKey, coin) => {
        const enabled =
            (engineKey === "scalping_engine" && scalpingSet.has(coin)) ||
            (engineKey === "nebula_horizon" && horizonSet.has(coin)) ||
            (engineKey === "signal_engine" && dreamSet.has(coin));
        if (!enabled) {
            return <td key={`${engineKey}-${coin}`} className="px-2 py-1 text-center text-gray-600">—</td>;
        }
        const mode = modeState.engines?.[engineKey]?.[coin] || modeState.globalMode;
        return (
            <td key={`${engineKey}-${coin}`} className="px-2 py-1">
                <select
                    id={`${engineKey}-${coin}`}
                    name={`${engineKey}-${coin}`}
                    value={mode}
                    onChange={(e) => handleChange(engineKey, coin, e.target.value)}
                    className="w-full rounded border border-gray-700 bg-gray-900 px-2 py-1 text-sm text-gray-100"
                    data-testid={`mode-select-${engineKey}-${coin}`}
                >
                    {MODE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            </td>
        );
    };

    const renderTable = () => {
        if (loadStatus === "unauthorized") {
            return (
                <div className="text-sm text-center text-red-300" data-testid="trading-settings-unauthorized">
                    Sign in to view trading mode controls.
                </div>
            );
        }
        if (loadStatus === "no_data") {
            return (
                <div className="text-sm text-center text-gray-400" data-testid="trading-settings-empty">
                    No trading mode configuration found yet.
                </div>
            );
        }
        if (loadStatus === "error") {
            return (
                <div className="text-sm text-center text-red-400" data-testid="trading-settings-error">
                    Unable to load settings.
                </div>
            );
        }
        if (loadingModes) {
            return (
                <div className="text-sm text-center text-gray-400" data-testid="trading-settings-loading">
                    Loading settings…
                </div>
            );
        }
        return (
            <div className="overflow-x-auto" data-testid="engine-runtime-table">
                <table className="min-w-full text-sm text-gray-200">
                    <thead>
                        <tr className="text-left text-gray-400">
                            <th className="px-2 py-2">Coin</th>
                            {ENGINE_DEFINITIONS.map((engine) => (
                                <th key={engine.key} className="px-2 py-2">
                                    {engine.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {allCoins.map((coin) => (
                            <tr key={coin} className="border-t border-gray-800">
                                <td className="px-2 py-2 font-semibold text-white">{coin}</td>
                                {ENGINE_DEFINITIONS.map((engine) => renderCell(engine.key, coin))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <StrategyOverlay open={open} onClose={onClose} title="Trading Mode">
            <div className="space-y-4">
                <div className="rounded-2xl border border-gray-800 bg-gray-900/70" data-testid="engine-runtime-card">
                    <button
                        type="button"
                        onClick={() => setExpanded((prev) => !prev)}
                        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                        data-testid="engine-runtime-toggle"
                    >
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <p className="text-[0.7rem] uppercase tracking-wide text-gray-400">Settings</p>
                                <span className="text-[0.7rem] text-gray-500">PIN required to change</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-semibold text-white">Engine Runtime Modes</h3>
                                <span className="text-xs text-gray-400" data-testid="engine-runtime-summary">
                                    {summary}
                                </span>
                            </div>
                            <p className="text-xs text-gray-400">
                                Adjust per-coin engine modes (dry / paper / live). PIN required to change.
                            </p>
                        </div>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            className={`h-4 w-4 text-gray-300 transition-transform duration-200 ${
                                expanded ? "rotate-90" : ""
                            }`}
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                    {expanded && (
                        <div className="border-t border-gray-800 px-4 pb-4 space-y-4">
                            {renderTable()}
                            <div className="flex items-center justify-between text-xs text-gray-400">
                                {saveError ? <span className="text-rose-300">{saveError}</span> : <span />}
                                {loadError && <span className="text-amber-300">{loadError}</span>}
                                <span className="flex-1" />
                                <button
                                    type="button"
                                    onClick={handleApply}
                                    className="rounded-md bg-cyan-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                                    disabled={disabledApply}
                                    data-testid="engine-runtime-apply"
                                >
                                    {saving ? "Applying…" : "Apply Changes"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={handleLogout}
                    className="mt-2 px-4 py-1 bg-red-700 rounded text-sm"
                >
                    {loggingOut ? "Logging out…" : "Logout"}
                </button>
            </div>
            {pinModalOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                    onClick={() => setPinModalOpen(false)}
                    role="dialog"
                    aria-modal="true"
                    data-testid="engine-pin-modal"
                >
                    <div
                        className="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl w-full max-w-md"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
                            <h3 className="text-lg font-semibold text-white">Enter PIN to apply</h3>
                            <button
                                type="button"
                                className="text-gray-400 hover:text-white"
                                onClick={() => setPinModalOpen(false)}
                                aria-label="Close PIN dialog"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="px-4 py-3 space-y-3 text-sm text-gray-100">
                            <p className="leading-snug">
                                Sensitive runtime mode changes require your session PIN.
                            </p>
                            <div className="space-y-1">
                                <label htmlFor="engine-pin-input" className="text-xs text-gray-300 uppercase tracking-wide">
                                    PIN
                                </label>
                                <input
                                    id="engine-pin-input"
                                    type="password"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    className="w-full rounded-md bg-gray-950 border border-gray-800 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                    value={pinValue}
                                    onChange={(e) => {
                                        setPinValue(e.target.value || "");
                                        if (pinError) setPinError("");
                                    }}
                                    autoFocus
                                    data-testid="engine-pin-input"
                                />
                                {pinError && (
                                    <div className="text-xs text-rose-200" data-testid="engine-pin-error">
                                        {pinError}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-800 bg-gray-950/50 rounded-b-xl">
                            <button
                                type="button"
                                className="px-3 py-2 text-sm rounded-md border border-gray-700 text-gray-200 hover:bg-gray-800"
                                onClick={() => setPinModalOpen(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="px-3 py-2 text-sm rounded-md bg-cyan-700 text-white font-semibold hover:bg-cyan-600 disabled:opacity-60"
                                onClick={handleConfirmPin}
                                data-testid="engine-pin-confirm"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </StrategyOverlay>
    );
}

TradingModeSettings.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};
